import type { SignageComponent } from '@/config/types';
import { warnOnce } from '@/lib/warnOnce';

/**
 * `components` + `slider.slides.length` -> the slide model `SliderLayout` renders.
 *
 * Port of the slide-assignment block in legacy `moduleLoader.vue`'s `setup()`:
 *
 * ```ts
 * props.components.forEach((component) => {
 *   if (component.slide !== undefined && Number.isInteger(component.slide)) {
 *     if (slider.slides[component.slide - 1] !== undefined) {
 *       if (slider.slides[component.slide - 1].components === undefined) {
 *         slider.slides[component.slide - 1].components = [];
 *         slider.slides[component.slide - 1].visible = visible;
 *         visible = false;
 *       }
 *       component.index = componentIndex;
 *       slider.slides[component.slide - 1].components.push(component);
 *       componentIndex++;
 *     }
 *   }
 * });
 * ```
 *
 * A pure function in its own module, and not a hook, because every property worth asserting
 * here — which component lands on which slide, which are dropped, what the index counter does
 * — is testable without a DOM.
 *
 * ## What is preserved
 *
 * - **`slide` is 1-based.** `slide: 1` is `slides[0]`.
 * - **Anything that does not resolve to a slide is dropped entirely.** No `slide` key, a
 *   non-integer `slide`, `slide: 0`, a negative one, or one past the end of `slides`: the
 *   component renders nowhere at all. That is legacy behaviour and it is preserved, but a
 *   widget that silently vanishes is undebuggable from a wall, so each one warns.
 * - **The index counter runs across slides, not per slide.** It is the key `useStableIds()`
 *   is called with, so per-slide numbering would collide three components onto one id
 *   (CLAUDE.md constraint 3). It also only advances for components that were *kept* — a
 *   dropped one does not consume a number, exactly as the legacy `componentIndex++` sat
 *   inside the `if`.
 *
 * ## What is fixed
 *
 * W-13: legacy writes `components` and `visible` into `slider.slides[n]` and `index` into
 * each component — it mutates the parsed config object. `SliderLayout` derives this model
 * instead and never touches its input. The config is shared (`ConfigProvider` hands the same
 * object to every consumer) and a mutation there is not merely untidy: a second pass over a
 * config that had already been grouped would push every component onto its slide twice.
 *
 * `visible` is not carried over. Legacy sets it on the first slide that receives components
 * and nothing — template, script or style — ever reads it back.
 */

/** One component on a slide, with the index its stable id is derived from. */
export type SlideComponent = {
  component: SignageComponent;
  /** Legacy `component.index`: a counter that runs across every slide, not per slide. */
  index: number;
};

export type Slide = {
  /** In config order. Empty when no component points at this slide — a legal, blank slide. */
  components: SlideComponent[];
};

export function buildSlides(components: SignageComponent[], slideCount: number): Slide[] {
  // Length is `slides.length` and not "the highest slide anyone points at": a `slides` entry
  // that no component references is still a slide, still gets its turn in the rotation, and
  // shows an empty screen for `interval` seconds. That is what the legacy `v-for` over
  // `slider.slides` does, and a config with a deliberate blank slide would break if this
  // collapsed it away.
  const slides: Slide[] = Array.from({ length: slideCount }, () => ({ components: [] }));

  let componentIndex = 0;

  for (const [position, component] of components.entries()) {
    const slide = resolveSlide(slides, component.slide);

    if (!slide) {
      // Keyed by the cause — the config node — not by the occurrence, so StrictMode's double
      // render logs one line rather than two.
      warnOnce(
        `slide-dropped:${String(position)}:${component.type}`,
        '[signage] component %d (%s) has slide %s, which is not one of the %d configured ' +
          'slides — it will not render anywhere. `slide` is 1-based.',
        position,
        component.type,
        String(component.slide),
        slideCount,
      );
      continue;
    }

    slide.components.push({ component, index: componentIndex });
    componentIndex++;
  }

  return slides;
}

/**
 * The slide a component belongs on, or `undefined` if it belongs on none.
 *
 * `Number.isInteger` is redundant against `componentSchema`, which types `slide` as
 * `z.number().int().optional()` and rejects a fractional one before this ever runs. It is
 * kept because it is the legacy guard and because this function is exported: the type says
 * `number | undefined`, and a `number` that is not an integer would otherwise index the array
 * with `1.5 - 1` and quietly resolve to nothing.
 */
function resolveSlide(slides: Slide[], slide: number | undefined): Slide | undefined {
  if (slide === undefined || !Number.isInteger(slide)) return undefined;
  return slides[slide - 1];
}
